import React from "react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  InputAdornment,
} from "@mui/material";
import { Button as Btn } from "../../../components/atoms/Button";
import theme from "../../../theme";
import { FlexRow, Spacer } from "../../../components";
import { useSnackbar } from "../../../context/SnackbarContext";
import type {
  ColorsCodeType,
  DonorTagged,
  ScholarshipApplicationDetailsProps,
  UserProps,
} from "../../../typings";
import { FaMoneyBillWave } from "react-icons/fa";
import { MdPersonAdd } from "react-icons/md";
import api from "../../../api/axiosInstance";
import { SCHOLARSHIP_APPLICATION_INITIAL_VALUES } from "../../../data/scholarship-applications";

interface DonorsModalProps {
  open: boolean;
  onClose: () => void;
  donorsTagged: DonorTagged[];
  scholarshipApplicationId: number;
  onRefresh: () => void;
}

export default function DonorsModal({
  open,
  onClose,
  donorsTagged,
  scholarshipApplicationId,
  onRefresh,
}: DonorsModalProps) {
  const { showSnackbar } = useSnackbar();
  const [selectedDonor, setSelectedDonor] = useState<number | "">("");
  const [donationAmount, setDonationAmount] = useState<string>("");
  const [donors, setDonors] = useState<UserProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDonors, setFetchingDonors] = useState(false);
  const [loadingAmount, setLoadingAmount] = useState(false);
  const [application, setApplication] = useState<ScholarshipApplicationDetailsProps>(
    SCHOLARSHIP_APPLICATION_INITIAL_VALUES
  );

  const taggedDonorIds =
    donorsTagged?.map((d: DonorTagged) => d.donor_tagged.id) || [];

  useEffect(() => {
    if (open) {
      fetchApplication();
      fetchDonors();
      setSelectedDonor("");
      setDonationAmount("");
    }
  }, [open]);

  const fetchDonors = async () => {
    setFetchingDonors(true);

    try {
      const response = await api.get("/users?role=DONOR");
      setDonors(response.data.data);
    } catch (err) {
      console.error("Failed to fetch donors:", err);
      showSnackbar("Failed to load donors. Please try again.", "error");
    } finally {
      setFetchingDonors(false);
    }
  };

  const fetchApplication = async () => {
    setLoadingAmount(true);
    try {
      const response = await api.get(
        `/scholarship-applications/${scholarshipApplicationId}/`
      );
      const application = response.data.data;
      setApplication(application);
    } catch (err) {
      console.error("Failed to fetch application details:", err);
      showSnackbar("Failed to load application details.", "error");
    } finally {
      setLoadingAmount(false);
    }
  };

  const totalAmountTagged = () => {
    let total = 0;
    application.donors_tagged.forEach((d) => {
      if (d.tagged_amount) total += d.tagged_amount;
    });
    return total;
  };

  const totalTaggedAmount = totalAmountTagged();
  const remainingAmount = application.amount_granted - totalTaggedAmount;





  const handleDonorChange = (event: any) => {
    setSelectedDonor(event.target.value);
  };

  const handleAmountChange = (event: any) => {
    const value = event.target.value.replace(/[^0-9]/g, "");

    if (value && Number(value) > remainingAmount) {
      showSnackbar("Amount cannot exceed available funds", "warning");
      return;
    }

    setDonationAmount(value);
  };

  const validateForm = () => {
    if (!selectedDonor) {
      showSnackbar("Please select a donor", "warning");
      return false;
    }

    if (!donationAmount || Number.parseInt(donationAmount) <= 0) {
      showSnackbar("Please enter a valid donation amount", "warning");
      return false;
    }

    if (Number.parseInt(donationAmount) > remainingAmount) {
      showSnackbar("Amount cannot exceed available funds", "warning");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        scholarship_application_id: scholarshipApplicationId,
        donors: [
          {
            donor_id: selectedDonor,
            amount: Number.parseInt(donationAmount),
          },
        ],
      };

      const response = await api.post(
        "/scholarship-applications/tag-donors/",
        payload
      );

      showSnackbar("Donor tagged successfully!", "success");

      // Refresh the applications list
      onRefresh();

      // Close the modal after a short delay to show the success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Failed to tag donor:", err);
      showSnackbar("Failed to tag donor. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            borderRadius: "6px",
            maxWidth: "550px",
            width: "500px",
          },
        }}
      >
        <DialogTitle
          style={{
            padding: "24px 24px 16px",
            fontSize: "24px",
            fontWeight: 600,
          }}
        >
          <FlexRow align="center" justify="space-between">
            <FlexRow align="center">
              <MdPersonAdd
                size={24}
                style={{
                  marginRight: "10px",
                  color: theme.palette.primary.main,
                }}
              />
              Tag Donor
            </FlexRow>
            <FlexRow align="center">
              <FlexRow align="center">
                <Typography variant="subtitle2" fontWeight="500">
                  Payable Amount:
                </Typography>
              </FlexRow>
              <Spacer horizontal={0.4} />
              {loadingAmount ? (
                <CircularProgress size={20} />
              ) : (
                <Typography
                  variant="subtitle2"
                  fontWeight="600"
                  color="primary.main"
                >
                  {remainingAmount}
                </Typography>
              )}
            </FlexRow>
          </FlexRow>
        </DialogTitle>
        <DialogContent style={{ padding: "0px 24px 16px" }}>
          <Typography
            variant="body2"
            style={{
              marginBottom: "20px",
              color: theme.palette.text.secondary,
            }}
          >
            Select a donor and specify the donation amount to tag them to this
            scholarship application.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="donor-select-label">Select Donor</InputLabel>
              <Select
                labelId="donor-select-label"
                id="donor-select"
                value={selectedDonor}
                onChange={handleDonorChange}
                label="Select Donor"
                disabled={fetchingDonors}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {fetchingDonors ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} style={{ margin: "0 auto" }} />
                  </MenuItem>
                ) : (
                  donors.map((donor: UserProps) => (
                    <MenuItem key={donor.id} value={donor.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>{donor.name}</span>
                        {taggedDonorIds.includes(donor.id) && (
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 2,
                              color: theme.palette.primary.main,
                              fontWeight: "bold",
                            }}
                          >
                            Already Tagged
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Donation Amount"
              variant="outlined"
              value={donationAmount}
              onChange={handleAmountChange}
              helperText={
                donationAmount && Number(donationAmount) > remainingAmount
                  ? "Amount exceeds Payable amount"
                  : ""
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaMoneyBillWave color={theme.palette.primary.main} />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter amount"
            />
          </Box>
        </DialogContent>
        <FlexRow
          justify="flex-end"
          align="center"
          style={{ padding: "15px 24px 24px" }}
        >
          <Btn
            buttonColor={theme.palette.colors["black"]["600"] as ColorsCodeType}
            textColor={theme.palette.colors["white"]["400"] as ColorsCodeType}
            onClick={onClose}
          >
            Cancel
          </Btn>
          <Spacer horizontal={1} />
          <Btn
            onClick={handleSave}
            disabled={!selectedDonor || !donationAmount || loading}
          >
            {loading ? (
              <>
                <CircularProgress
                  size={16}
                  style={{
                    marginRight: 8,
                    color: theme.palette.colors.white[400] as string,
                  }}
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Btn>
        </FlexRow>
      </Dialog>

    </>
  );
}
